import { inject, Injectable } from '@angular/core'
import { delay, map, Observable, of, switchMap, concat } from 'rxjs'
import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http'

export type FileUploadType = 'agenda' | 'recording' | 'custom-document' | 'board-pack'

const uploadErrorMessages: Record<string, string> = {
  PASSWORD_PROTECTED_PDF: 'CALLS.CALL_INFO.DOCUMENT_UPLOAD.PASSWORD_PROTECTED_PDF',
  TOO_MANY_PAGES: 'CALLS.CALL_INFO.DOCUMENT_UPLOAD.TOO_MANY_PAGES',
  DUPLICATE_RESOURCE_NAME: 'CALLS.CALL_INFO.DOCUMENT_UPLOAD.DUPLICATE_FILE_NAME',
  DOCUMENT_CONVERSION_FAILED: 'CALLS.CALL_INFO.DOCUMENT_UPLOAD.CONVERSION_FAILED',
  DOCUMENT_TOO_LARGE: 'CALLS.CALL_INFO.DOCUMENT_UPLOAD.DOCUMENT_TOO_LARGE',
  DOCUMENT_EXCEEDS_CONTENT_CAPACITY: 'CALLS.CALL_INFO.DOCUMENT_UPLOAD.EXCEEDS_CONTENT_CAPACITY',
  DOCUMENT_TOKEN_COUNT_FAILED: 'CALLS.CALL_INFO.DOCUMENT_UPLOAD.TOKEN_COUNT_FAILED',
  BOARD_PACK_TOO_LARGE: 'CALLS.CALL_INFO.DOCUMENT_UPLOAD.BOARD_PACK_TOO_LARGE',
  BOARD_PACK_PROCESSING_FAILED: 'CALLS.CALL_INFO.DOCUMENT_UPLOAD.BOARD_PACK_PROCESSING_FAILED',
  CUSTOM_DOCUMENT_PROCESSING_FAILED: 'CALLS.CALL_INFO.DOCUMENT_UPLOAD.CUSTOM_DOCUMENT_PROCESSING_FAILED',
}

export function getUploadErrorMessage(error: any): string | null {
  return uploadErrorMessages[error?.error?.message] ?? null
}

export type UploadProgress = {
  progress: number
  uploadKey: string | null
  remainingTimeSeconds: number | null
  isScanning: boolean
}

type PresignedUrlResponse = {
  uploadUrl: string
  uploadKey: string
}

type UploadStatusResponse = {
  status: 'pending' | 'ready'
}

const SCAN_POLL_INTERVAL_MS = 2_000

@Injectable({
  providedIn: 'root',
})
export class UploadsService {
  private http = inject(HttpClient)

  uploadCustomDocument(chosenFile: File): Observable<UploadProgress> {
    return this.upload('custom-document', chosenFile)
  }

  uploadAgenda(chosenFile: File): Observable<UploadProgress> {
    return this.upload('agenda', chosenFile)
  }

  uploadRecording(chosenFile: File): Observable<UploadProgress> {
    return this.uploadViaPresignedUrl(chosenFile)
  }

  upload(type: FileUploadType, chosenFile: File): Observable<UploadProgress> {
    const formData: FormData = new FormData()
    formData.append('file', chosenFile)
    const startTime = Date.now()
    return this.http
      .post(`/api/uploads/${type}`, formData, {
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        switchMap((event: HttpEvent<any>) => {
          switch (event.type) {
            case HttpEventType.UploadProgress: {
              const progress = event.total ? Math.round((100 * event.loaded) / event.total) : 0
              const remainingTimeSeconds = this.calculateRemainingTime(startTime, event.loaded, event.total ?? null)
              return of({ progress, uploadKey: null, remainingTimeSeconds, isScanning: false })
            }
            case HttpEventType.Response:
              return concat(of({ progress: 100, uploadKey: null, remainingTimeSeconds: null, isScanning: true }), this.pollUploadStatus(event.body.uploadKey))
            default:
              return of({ progress: 0, uploadKey: null, remainingTimeSeconds: null, isScanning: false })
          }
        })
      )
  }

  private uploadViaPresignedUrl(chosenFile: File): Observable<UploadProgress> {
    return this.http.post<PresignedUrlResponse>('/api/uploads/recording/presigned-url', {}).pipe(
      switchMap((presignedResponse) => {
        const startTime = Date.now()
        const upload$ = this.http
          .put(presignedResponse.uploadUrl, chosenFile, {
            reportProgress: true,
            observe: 'events',
            headers: { 'Content-Type': 'application/octet-stream' },
          })
          .pipe(
            map((event: HttpEvent<any>) => {
              switch (event.type) {
                case HttpEventType.UploadProgress: {
                  const progress = event.total ? Math.round((100 * event.loaded) / event.total) : 0
                  const remainingTimeSeconds = this.calculateRemainingTime(startTime, event.loaded, event.total ?? null)
                  return { progress, uploadKey: null, remainingTimeSeconds, isScanning: false }
                }
                case HttpEventType.Response:
                  return { progress: 100, uploadKey: null, remainingTimeSeconds: null, isScanning: true }
                default:
                  return { progress: 0, uploadKey: null, remainingTimeSeconds: null, isScanning: false }
              }
            })
          )

        const pollScanStatus$ = this.pollUploadStatus(presignedResponse.uploadKey)

        return concat(upload$, pollScanStatus$)
      })
    )
  }

  private pollUploadStatus(uploadKey: string): Observable<UploadProgress> {
    return of(null).pipe(
      delay(SCAN_POLL_INTERVAL_MS),
      switchMap(() => this.http.get<UploadStatusResponse>(`/api/uploads/${uploadKey}/status`)),
      switchMap((response) => {
        if (response.status === 'ready') {
          return of({ progress: 100, uploadKey, remainingTimeSeconds: null, isScanning: false })
        }
        return concat(of({ progress: 100, uploadKey: null, remainingTimeSeconds: null, isScanning: true }), this.pollUploadStatus(uploadKey))
      })
    )
  }

  private calculateRemainingTime(startTime: number, loaded: number, total: number | null): number | null {
    if (total === null || loaded === 0) {
      return null
    }
    const elapsedTimeSeconds = (Date.now() - startTime) / 1000
    if (elapsedTimeSeconds < 0.5) {
      return null
    }
    const bytesPerSecond = loaded / elapsedTimeSeconds
    const remainingBytes = total - loaded
    const remainingTimeSeconds = Math.ceil(remainingBytes / bytesPerSecond)
    return remainingTimeSeconds
  }
}
